import * as React from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { DocView, DocumentWidgetItem } from "../myDocumentsSlice";
import { getStatusEntry } from "../../documents/utils/documentStatusUi";
import { DocumentStatusChip } from "../../documents/controls/DocumentStatusChip";
import { DocumentAvatar } from "../../documents/controls/DocumentAvatar";
import { Localized } from "@fluent/react";

const fmt = (iso?: string | null) => {
    if (!iso) return "—";
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return "—";
    return new Date(t).toLocaleDateString();
};

const leftBarColor = (d: DocumentWidgetItem) => {
    const entry = getStatusEntry(d.status);
    return `${entry.color}.main` as const;
};

export const DocumentsWidgetTabbed: React.FC<{
    title: string;
    icon: React.ReactNode;
    recent: DocumentWidgetItem[];
    progress: DocumentWidgetItem[];
    waiting: DocumentWidgetItem[];
    onOpen: (d: DocumentWidgetItem) => void;
}> = ({ title, icon, recent, progress, waiting, onOpen }) => {
    const [view, setView] = React.useState<DocView>("recent");

    const dataByView: Record<DocView, DocumentWidgetItem[]> = {
        recent,
        progress,
        waiting,
    };

    const data = dataByView[view];
    const top = data.slice(0, 7);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                minHeight: { xs: 260, md: 320 },
            }}
        >
            <Box
                sx={{
                    mx: -1.5,
                    mt: -1.5,
                    mb: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                        }}
                    >
                        {icon}
                    </Box>

                    <Typography variant="h6" fontWeight={800} noWrap>
                        {title}
                    </Typography>

                    <Box flexGrow={1} />

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view}
                        onChange={(_, v) => v && setView(v)}
                    >
                        <Tooltip title={
                            <span>
                                <Localized id="document-widget-tab-recent-hint">
                                    _recent
                                </Localized>
                            </span>}>
                            <ToggleButton value="recent">
                                <AccessTimeIcon fontSize="small" />

                                <Typography variant="caption" fontWeight={800}>
                                    <Localized id="document-widget-tab-recent">
                                        <span>_Recent</span>
                                    </Localized>
                                </Typography>

                                <Chip size="small" label={recent.length} sx={{ height: 18, fontSize: 11 }} />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title={
                            <span>
                                <Localized id="document-widget-tab-progress-hint">
                                    _In progress
                                </Localized>
                            </span>}
                        >
                            <ToggleButton value="progress">
                                <WorkOutlineIcon fontSize="small" />

                                <Typography variant="caption" fontWeight={800}>
                                    <Localized id="document-widget-tab-progress">
                                        <span>In progress</span>
                                    </Localized>
                                </Typography>

                                <Chip size="small" label={progress.length} sx={{ height: 18, fontSize: 11 }} />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title={
                            <span>
                                <Localized id="document-widget-tab-waiting-hint">
                                    _waiting for input
                                </Localized>
                            </span>
                        }>
                            <ToggleButton value="waiting">
                                <HourglassEmptyIcon fontSize="small" />

                                <Typography variant="caption" fontWeight={800}>
                                    <Localized id="document-widget-tab-waiting">
                                        <span>Waiting</span>
                                    </Localized>
                                </Typography>

                                <Chip size="small" label={waiting.length} sx={{ height: 18, fontSize: 11 }} />
                            </ToggleButton>
                        </Tooltip>

                    </ToggleButtonGroup>
                </Stack>
            </Box>

            <Box sx={{ overflow: "auto", minHeight: 0 }}>
                <List disablePadding>
                    {top.map((d, idx) => {
                        const metaRight =
                            view === "recent" ? `Opened: ${fmt(d.lastOpenedAt ?? null)}` : null;

                        return (
                            <React.Fragment key={`${d.companyId}-${d.id}`}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        <Tooltip title={<Localized id="open"><span>_Open</span></Localized>}>
                                            <IconButton edge="end" size="small" onClick={() => onOpen(d)}>
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                    sx={{
                                        px: 1,
                                        alignItems: "flex-start",
                                        "&:hover": { bgcolor: "action.hover" },
                                        position: "relative",
                                        "&:before": {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            top: 8,
                                            bottom: 8,
                                            width: 3,
                                            borderRadius: 1,
                                            bgcolor: leftBarColor(d),
                                        },
                                    }}
                                >
                                    <ListItemButton sx={{ py: 0.25 }} onClick={() => onOpen(d)}>
                                        <ListItemIcon sx={{ marginRight: 2 }}>
                                            <DocumentAvatar
                                                type={d.type}
                                                documentKind={d.kind}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "text.secondary",
                                                            fontWeight: 600,
                                                            letterSpacing: ".04em",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {d.number}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {d.title}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                    sx={{ mt: 0.25 }}
                                                >
                                                    <DocumentStatusChip status={d.status} />                                                    {metaRight && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            · {metaRight}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>

                                {idx < top.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        );
                    })}

                    {top.length === 0 && (
                        <Box px={1} py={3}>
                            <Typography variant="body2" color="text.secondary">
                                No documents in this view.
                            </Typography>
                        </Box>
                    )}
                </List>
            </Box>
        </Paper>
    );
};